import { UOM_CONVERSION_PRECISION, UOM_RENDER_PRECISION } from "@/constants/formats"
import {
  type Company,
  CompanyDefaultUnitOfMeasurement,
  CompanyPreferredMeasurementSystemEnum,
  type Maybe,
  type Product,
  type UnitOfMeasurement,
} from "@/graphql/codegen/graphql"
import { type ProductSelectionItem } from "@/screens/Procurement/Supplier/types/types"
import { type UomAmountWithId } from "@/types/convertion"

const KILOGRAMS_TO_POUNDS = 2.20462

export function isUsUom(uomId: string): boolean {
  return uomId?.startsWith("USSTD")
}

export function isMetricUom(uomId: string): boolean {
  return uomId?.startsWith("METRIC")
}

export function isSameMeasurementSystem(uomId1: string, uomId2: string): boolean {
  return (isUsUom(uomId1) && isUsUom(uomId2)) || (isMetricUom(uomId1) && isMetricUom(uomId2))
}

export function extractDefaultProductUom(company: Company, product: ProductSelectionItem): string {
  return (
    company.preferredMeasurementSystem === CompanyPreferredMeasurementSystemEnum.UsStandard
      ? product.packageUsWeightUom
      : product.packageMetricWeightUom
  ) as string
}

function getDefaultUoms(usWeightUom?: string | null, metricWeightUom?: string | null) {
  return {
    usUom: usWeightUom ?? CompanyDefaultUnitOfMeasurement.UsstdWeightPound,
    metricUom: metricWeightUom ?? CompanyDefaultUnitOfMeasurement.MetricWeightKilogram,
  }
}

const POUND_TO_SHORT_TON = 2_000
const KILOGRAM_TO_METRIC_TON = 1_000
const UOM_MAPPING = new Map<CompanyDefaultUnitOfMeasurement, number>([
  [CompanyDefaultUnitOfMeasurement.MetricWeightTon, KILOGRAM_TO_METRIC_TON],
  [CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon, POUND_TO_SHORT_TON],
])

export const convertFromTon = (value: number, tonUomId: string): number => {
  return value * (UOM_MAPPING.get(tonUomId as CompanyDefaultUnitOfMeasurement) || 1)
}

export const convertToTon = (value: number, tonUomId: string): number => {
  return value / (UOM_MAPPING.get(tonUomId as CompanyDefaultUnitOfMeasurement) || 1)
}

export const isTonUom = (uomId: string): boolean => {
  return [
    CompanyDefaultUnitOfMeasurement.MetricWeightTon,
    CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon,
  ].includes(uomId as CompanyDefaultUnitOfMeasurement)
}

export const isLbKgUom = (uomId: string): boolean => {
  return [
    CompanyDefaultUnitOfMeasurement.MetricWeightKilogram,
    CompanyDefaultUnitOfMeasurement.UsstdWeightPound,
  ].includes(uomId as CompanyDefaultUnitOfMeasurement)
}

export const isProductInTon = (product: Product): boolean => {
  if (!product?.palletUsWeightUom || !product?.packageUsWeightUom) {
    return false
  }

  return isTonUom(product?.palletUsWeightUom) || isTonUom(product?.packageUsWeightUom)
}

const convertMetricTonPrice = (amount: number, productIsInTon: boolean): UomAmountWithId => ({
  amount: productIsInTon ? amount : amount * 2,
  uomId: CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon,
})

const convertUsTonPrice = (amount: number, productIsInTon: boolean): UomAmountWithId => ({
  amount: productIsInTon ? amount : amount / 2,
  uomId: CompanyDefaultUnitOfMeasurement.MetricWeightTon,
})

const convertMetricLbKgPrice = (amount: number, productIsInTon: boolean): UomAmountWithId => ({
  amount: productIsInTon ? amount / 2 : amount,
  uomId: CompanyDefaultUnitOfMeasurement.UsstdWeightPound,
})

const convertUsLbKgPrice = (amount: number, productIsInTon: boolean): UomAmountWithId => ({
  amount: productIsInTon ? amount * 2 : amount,
  uomId: CompanyDefaultUnitOfMeasurement.MetricWeightKilogram,
})

export const convertPrice = ({
  currentPrice,
  product,
  pricingUomId,
}: {
  currentPrice: number
  product: Product | undefined | null
  pricingUomId: string
}): UomAmountWithId => {
  const { uomId: convertedCurrentPriceUomId } = convertUoMQuantity(currentPrice, pricingUomId, product, "package")
  const { amount } = convertUoMQuantity(currentPrice, convertedCurrentPriceUomId, product, "package")

  const productIsInTon = product ? isProductInTon(product) : false
  const uomIsMetric = isMetricUom(pricingUomId)

  if (isTonUom(pricingUomId)) {
    return uomIsMetric ? convertMetricTonPrice(amount, productIsInTon) : convertUsTonPrice(amount, productIsInTon)
  }

  if (isLbKgUom(pricingUomId)) {
    return uomIsMetric ? convertMetricLbKgPrice(amount, productIsInTon) : convertUsLbKgPrice(amount, productIsInTon)
  }

  return { amount, uomId: convertedCurrentPriceUomId }
}

export const calculateLineTotal = ({
  currentPrice,
  pricingUomId,
  product,
  quantity,
  uomId,
}: {
  currentPrice: number
  pricingUomId: string
  product: Product | undefined | null
  quantity: number
  uomId: string
}): number => {
  const { amount: oppositePrice, uomId: oppositeRenderUomId } = convertPrice({
    currentPrice,
    product,
    pricingUomId,
  })

  if (pricingUomId === uomId) {
    return quantity * currentPrice
  }

  let price = 1
  let calcQuantity = quantity

  const isSameSystem = isSameMeasurementSystem(uomId, pricingUomId)

  if (isLbKgUom(uomId) && isTonUom(pricingUomId)) {
    price = isSameSystem ? convertToTon(currentPrice, pricingUomId) : convertToTon(oppositePrice, oppositeRenderUomId)
  } else if (isTonUom(uomId) && isLbKgUom(pricingUomId)) {
    calcQuantity = convertFromTon(quantity, uomId)
    price = isSameSystem ? currentPrice : oppositePrice
  } else {
    price = oppositePrice
  }

  return calcQuantity * price
}

// TODO: https://robotsandpencils.atlassian.net/browse/AK-3638 - UoM conversion util - refactor types
export const convertUoMQuantity = (
  quantity: number | null,
  sourceUomId: string,
  product: Product | undefined | null,
  packaging: "package" | "pallet"
): {
  amount: number
  uomId: string
} => {
  if (!product) {
    return { amount: 0, uomId: "" }
  }

  let parsedQuantity = parseFloat(quantity?.toString() || "0")

  const {
    packageMetricWeight,
    packageMetricWeightUom,
    packageUsWeight,
    packageUsWeightUom,
    palletMetricWeight,
    palletMetricWeightUom,
    palletUsWeight,
    palletUsWeightUom,
  } = product

  if (!sourceUomId) {
    return { amount: 0, uomId: "" }
  }

  const isUs = isUsUom(sourceUomId)

  let usWeight = 0
  let metricWeight = 0
  let usUom = ""
  let metricUom = ""

  if (packaging === "package") {
    if (!packageMetricWeight || !packageUsWeight) {
      usWeight = 1
      metricWeight = 1 / KILOGRAMS_TO_POUNDS
    } else {
      usWeight = packageUsWeight
      metricWeight = packageMetricWeight
    }

    const defaultUoms = getDefaultUoms(packageUsWeightUom, packageMetricWeightUom)
    usUom = defaultUoms.usUom
    metricUom = defaultUoms.metricUom
  } else {
    if (!palletMetricWeight || !palletUsWeight) {
      usWeight = 1
      metricWeight = 1 / KILOGRAMS_TO_POUNDS
    } else {
      usWeight = palletUsWeight
      metricWeight = palletMetricWeight
    }

    const defaultUoms = getDefaultUoms(palletUsWeightUom, palletMetricWeightUom)
    usUom = defaultUoms.usUom
    metricUom = defaultUoms.metricUom
  }

  const valueFrom = isUs ? metricWeight : usWeight
  const valueTo = isUs ? usWeight : metricWeight

  const ratio = valueFrom / valueTo

  if (palletUsWeightUom && !isTonUom(palletUsWeightUom)) {
    parsedQuantity = convertFromTon(parsedQuantity, sourceUomId)
  }

  return {
    amount: (parsedQuantity * ratio).toFixed(UOM_CONVERSION_PRECISION),
    uomId: isUs ? metricUom : usUom,
  }
}

export function convertAmountToTon(quantity: number, sourceUomId: string, targetUomId: string): UomAmountWithId {
  let uomId = targetUomId

  if (sourceUomId === CompanyDefaultUnitOfMeasurement.MetricWeightTon) {
    uomId = CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon
  } else if (sourceUomId === CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon) {
    uomId = CompanyDefaultUnitOfMeasurement.MetricWeightTon
  }

  return { amount: convertToTon(quantity, uomId), uomId }
}

export function convertAmountFromTon(quantity: number, sourceUomId: string, targetUomId: string): UomAmountWithId {
  let uomId = targetUomId

  if (sourceUomId === CompanyDefaultUnitOfMeasurement.MetricWeightKilogram) {
    uomId = CompanyDefaultUnitOfMeasurement.UsstdWeightPound
  } else if (sourceUomId === CompanyDefaultUnitOfMeasurement.UsstdWeightPound) {
    uomId = CompanyDefaultUnitOfMeasurement.MetricWeightKilogram
  }

  return { amount: convertFromTon(quantity, uomId), uomId }
}

export const getLbKgRatio = (product: Product | undefined | null): number => {
  if (!product) {
    return 1
  }

  const { packageMetricWeight, packageUsWeight } = product

  if (!packageMetricWeight || !packageUsWeight) {
    return 1
  }

  return (POUND_TO_SHORT_TON * packageUsWeight) / (KILOGRAM_TO_METRIC_TON * packageMetricWeight)
}

export const getTonUomId = (uomId: string): string => {
  return uomId === CompanyDefaultUnitOfMeasurement.UsstdWeightPound
    ? CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon
    : CompanyDefaultUnitOfMeasurement.MetricWeightTon
}

export const getBaseUomId = (uomId: string): string => {
  return uomId === CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon
    ? CompanyDefaultUnitOfMeasurement.UsstdWeightPound
    : CompanyDefaultUnitOfMeasurement.MetricWeightKilogram
}

export const convertPreviewQuantity = ({
  quantityInUom,
  uomId,
  product,
}: {
  quantityInUom: number
  uomId: string
  product: Product | undefined | null
}): UomAmountWithId => {
  let { amount: convertedQuantity, uomId: convertedUomId } = convertUoMQuantity(
    quantityInUom,
    uomId,
    product,
    "package"
  )

  if (isTonUom(uomId) && product && !isProductInTon(product)) {
    const { amount: convertedTonQuantity, uomId: convertedTonUomId } = convertAmountToTon(
      convertedQuantity,
      uomId,
      convertedUomId
    )

    convertedQuantity = convertedTonQuantity
    convertedUomId = convertedTonUomId
  }

  if (!isTonUom(uomId) && product && isProductInTon(product)) {
    const { uomId: convertedTonUomId } = convertAmountFromTon(convertedQuantity, uomId, convertedUomId)

    const lbKgRatio = getLbKgRatio(product)

    convertedQuantity =
      quantityInUom * (uomId === CompanyDefaultUnitOfMeasurement.MetricWeightKilogram ? lbKgRatio : 1 / lbKgRatio)
    convertedUomId = convertedTonUomId
  }

  return { amount: convertedQuantity, uomId: convertedUomId }
}

export function createUomsList(
  uoms: UnitOfMeasurement[],
  { packageMetricWeightUom, packageUsWeightUom }: Pick<Product, "packageMetricWeightUom" | "packageUsWeightUom">
): UnitOfMeasurement[] {
  const { usUom: usUomId, metricUom: metricUomId } = getDefaultUoms(packageUsWeightUom, packageMetricWeightUom)

  const uomsList: UnitOfMeasurement[] = []

  function addExtraUoM(packageUomId: Maybe<string> | undefined, uomId: string, uomToAdd: string): void {
    if (!packageUomId || packageUomId !== uomId) {
      return
    }

    const uom = uoms.find((uom) => uom.id === uomToAdd)

    if (!uom) {
      return
    }

    uomsList.push(uom)
  }

  const findById = (id: string) => (unit: UnitOfMeasurement) => unit.id === id

  const usUom = uoms.find(findById(usUomId))

  if (usUom) {
    uomsList.push(usUom)
  }

  addExtraUoM(
    packageUsWeightUom,
    CompanyDefaultUnitOfMeasurement.UsstdWeightPound,
    CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon
  )

  addExtraUoM(
    packageUsWeightUom,
    CompanyDefaultUnitOfMeasurement.UsstdWeightShortTon,
    CompanyDefaultUnitOfMeasurement.UsstdWeightPound
  )

  const metricUom = uoms.find(findById(metricUomId))

  if (metricUom) {
    uomsList.push(metricUom)
  }

  addExtraUoM(
    packageMetricWeightUom,
    CompanyDefaultUnitOfMeasurement.MetricWeightKilogram,
    CompanyDefaultUnitOfMeasurement.MetricWeightTon
  )

  addExtraUoM(
    packageMetricWeightUom,
    CompanyDefaultUnitOfMeasurement.MetricWeightTon,
    CompanyDefaultUnitOfMeasurement.MetricWeightKilogram
  )

  return uomsList
}

export const getConvertedWeightPerPallet = (targetUomId: string, product: Product): number => {
  let usWeight = 0
  let metricWeight = 0

  const { palletUsWeight, palletMetricWeight } = product

  if (!palletMetricWeight || !palletUsWeight) {
    usWeight = 1
    metricWeight = 1 / KILOGRAMS_TO_POUNDS
  } else {
    usWeight = palletUsWeight
    metricWeight = palletMetricWeight
  }

  return isUsUom(targetUomId) ? usWeight : metricWeight
}

export const parseInputQuantity = (quantityInUom: number, isUserInput: boolean): string | number => {
  const value = quantityInUom
  let formattedValue: string | number = 0

  if (isUserInput && !value) {
    formattedValue = ""
  } else if (value) {
    formattedValue = Number.isInteger(value) ? value.toString() : value.toFixed(UOM_RENDER_PRECISION)
    formattedValue = parseFloat(formattedValue.toString())
  }

  return formattedValue
}
